import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'glass' | 'primary' | 'secondary' | 'danger';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-control px-4 py-2.5 text-[11px] font-extrabold uppercase tracking-[0.04em] transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-45';

const VARIANTS: Record<ButtonVariant, string> = {
  glass:
    'border border-white/25 bg-white/15 text-white backdrop-blur-md hover:border-primary/60 hover:bg-primary/25 hover:text-primary',
  primary: 'border-none bg-primary text-on-primary hover:bg-primary-hover',
  secondary:
    'border border-border bg-white/5 text-content hover:border-border-strong hover:bg-white/10',
  /** Confirms a destructive or one-way action. */
  danger: 'border-none bg-danger text-on-danger hover:bg-danger/85',
};

/** Shared so buttons and button-styled links stay visually identical. */
export const buttonStyles = (
  variant: ButtonVariant = 'glass',
  fullWidth = false,
  className?: string,
) => cn(BASE, VARIANTS[variant], fullWidth && 'w-full', className);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  fullWidth?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'glass',
  fullWidth = false,
  className,
  type = 'button',
  children,
  ...props
}: ButtonProps) {
  return (
    <button type={type} className={buttonStyles(variant, fullWidth, className)} {...props}>
      {children}
    </button>
  );
}

export interface ButtonLinkProps {
  to: string;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Navigation that looks like a button.
 *
 * A real anchor rather than a button with an onClick, so middle-click,
 * open-in-new-tab and hover previews all work.
 */
export function ButtonLink({
  to,
  variant = 'glass',
  fullWidth = false,
  className,
  children,
}: ButtonLinkProps) {
  return (
    <Link to={to} className={buttonStyles(variant, fullWidth, className)}>
      {children}
    </Link>
  );
}
